import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    HostListener,
    inject,
    input,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { NCatalogUi } from '@df/catalog-ui/df-catalog-ui.config';
import { TAttrOptionSelection } from '@df/catalog-ui/listing/listing';
import { IAttributeConfDataOption } from '@df/catalog-ui/product/configurable-options/product-configurable-options.component.def';
import { ProductComponentDef } from '@df/catalog-ui/product/product.component.def';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { EdependencyLevel } from '@df/catalog/model';
import { IProductConfigurableSelection, IProductDataV2 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { CacheService } from '@df/core/cache/cache';
import { DfEventService } from '@df/core/service/df-event';
import { Status } from '@df/core/status';
import { AbService } from '@df/module-ab/ab.service';
import { KlevuSearch } from '@df/module-klevu-search/klevu-interfaces';
import { injectTimeoutService, TimeoutService } from '@df/ng/timeout.service';
import { BasketService } from '@df/session/api/basket/basket.service';
import { WishlistItem } from '@df/session/api/wishlist/wishlist-item';
import { filterMediaByLabel } from '@df/ui/common/filter-media-by-label-pipe';
import { VisibilityService } from '@df/ui/common/visibility.service';
import { Media } from '@df/ui/display/media/interfaces';
import { VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { DraggingService } from '@df/ui/dragging-service';
import { Nexus } from '@tomandco/nexus-api-types';
import compact from 'lodash-es/compact';
import find from 'lodash-es/find';
import { Subject } from 'rxjs/internal/Subject';
import { Subscription } from 'rxjs/internal/Subscription';
import { interval } from 'rxjs/internal/observable/interval';
import { skip } from 'rxjs/internal/operators/skip';
import { take } from 'rxjs/internal/operators/take';
import { takeWhile } from 'rxjs/internal/operators/takeWhile';
import { BASKET_ADD_NOTICE_CLOSE_EVENT_NAME } from '../../../basket/basket-add-notice-modal.component';
import { IColorMapEntry, ProductDecorator } from '../../model/product/product.decorator';
import { ProductListingVideoService } from '../listing/product-listing-video.service';
import { ProductListingDecorator } from '../listing/product-listing.decorator';
import { WishlistStatus } from '../wishlist/wishlist.interface';
import { ProductService } from './product.service';

export interface ListingImage {
    image: string;
    index?: number;
    video?: string;
}

export const LISTING_IMAGE_LABEL_PATTERN = new RegExp(/base|alternative|model/);

export interface ProductEventData {
    product: ProductDecorator<IProductDataV2>;
    index?: number;
    status?: WishlistStatus;
    loading?: boolean;
    option?: IAttributeConfDataOption;
    callback?: (component: ProductAbstract) => void;
}

declare module '@df/core/service/df-event' {
    interface IEventType {
        productClosePreview: DfEventConfig<'productClosePreview', ProductEventData, void | undefined>;
        productOpenPreview: DfEventConfig<'productOpenPreview', ProductEventData, void | undefined>;
        productPreparePreview: DfEventConfig<'productPreparePreview', ProductEventData, void | undefined>;
        productWishlistAdd: DfEventConfig<'productWishlistAdd', ProductEventData, void | undefined>;
        productWishlistClosePreview: DfEventConfig<'productWishlistClosePreview', ProductEventData, void | undefined>;
        productWishlistOpenPreview: DfEventConfig<'productWishlistOpenPreview', ProductEventData, void | undefined>;
        productWishlistPreparePreview: DfEventConfig<'productWishlistPreparePreview', ProductEventData, void | undefined>;
    }
}

export const PRODUCT_CLOSE_PREVIEW_EVENT_NAME = 'productClosePreview';
export const PRODUCT_OPEN_PREVIEW_EVENT_NAME = 'productOpenPreview';
export const PRODUCT_PREPARE_PREVIEW_EVENT_NAME = 'productPreparePreview';
export const PRODUCT_WISHLIST_ADD_EVENT_NAME = 'productWishlistAdd';
export const PRODUCT_WISHLIST_CLOSE_PREVIEW_EVENT_NAME = 'productWishlistClosePreview';
export const PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME = 'productWishlistOpenPreview';
export const PRODUCT_WISHLIST_PREPARE_PREVIEW_EVENT_NAME = 'productWishlistPreparePreview';

@Directive()
export class ProductAbstract extends ProductComponentDef<ProductDecorator<IProductDataV2>> implements OnInit, OnChanges, OnDestroy {
    priority = input(false);

    /** @internal Currently selected color */
    protected _currentColor?: number;

    /** @internal current slides loaded map */
    protected _slidesLoaded = new Map<string, boolean>();

    /**
     * Configurable product related to currently selected product
     */
    private currentColorProduct: ProductDecorator<IProductDataV2> | undefined;

    labelDiscount = false;
    listingPortraitImages?: ListingImage[];
    listingLandscapeImages?: ListingImage[];
    current: number;

    setColourWithDelay: (color: number) => void;

    /**
     * If user had any interaction with component (hover, click, touch)
     */
    private _hadInteraction = false;

    /**
     * Prepare preview
     */
    readonly _previewStatus = new Status();

    /**
     * Current model price
     */
    currentPrice: number;

    /**
     * Current model org price
     */
    currentOrgPrice: number;

    /**
     * Current model is from price
     */
    isFromPrice?: boolean;

    /**
     * Hash with selected colour to link to PP properly
     */
    urlFragment?: string;

    @Input()
    override listing?: ProductListingDecorator<ProductDecorator<IProductDataV2>>;

    /**
     * If true - Block product card carousel
     */
    @Input()
    disableCarousel = false;

    /**
     * Force load slider
     */
    @Input()
    forceLoad = false;

    /**
     * Hide product price
     */
    @Input()
    hidePrice = false;

    /**
     * Hide product name
     */
    @Input()
    hideName = false;

    /**
     * Hide product label
     */
    @Input()
    hideLabel = false;

    /**
     * Hide product video
     */
    @Input()
    hideVideo = false;

    /**
     * View base image with labels `model-base`
     */
    @Input()
    modelView = false;

    /**
     * View base image with label `(base|model)-alternative`
     */
    @Input()
    altView = false;

    /**
     * Wishlist item
     * Optionally passed, only on wishlist
     */
    @Input()
    wishlistItem?: WishlistItem;

    /**
     * Wishlist carousel item
     * Optionally passed, only on wishlist carousel
     */
    @Input()
    wishlistCarouselItem = false;

    @Input()
    wishlistSmallMessage = false;

    @Input()
    sendTracking = false;

    @Input()
    category?: Category<ICategoryDataV3>;

    @Input()
    klevuRecommendationId?: string;

    /**
     * Optional search term if showing from the search page
     */
    @Input()
    searchTerm?: string;

    @Input()
    isAccountPage = false;

    @Input()
    isSearchOutlet = false;

    @Input()
    hidePrefix = false;

    @Input()
    listingSize = 2;

    @Input('sizes')
    _sizes?: string | string[];

    @ViewChild('addToBasketTpl')
    protected addToBasketTpl!: TemplateRef<any>;

    /**
     * If component is ready to show preview
     */
    previewReady = false;

    /**
     * If preview was requested (button was clicked)
     */
    requestedPreview = false;

    /**
     * Parent model change subscription
     */
    private parentSubscription?: Subscription;
    private videoCheckSubscription?: Subscription;
    private shouldLoopSubscription?: Subscription;

    detectChanges$ = new Subject<void>();

    basketAdded = false;
    emailMeWhenInStock = false;
    isPlaying = false;
    isVideoBuffered = false;
    protected playerInstance?: any;
    protected playerStateSubscription?: Subscription;
    protected shouldLoop = false;
    protected basketAddNotifyTimeout?: number;
    protected abServiceSubscription?: Subscription;

    protected moduleConfig = inject(NCatalogUi.OPTIONS);
    protected dfEventService = inject(DfEventService);
    protected productService = inject(ProductService);
    protected basketService = inject(BasketService);
    protected abService = inject(AbService);
    protected readonly disposableTimeoutService = injectTimeoutService();

    constructor(
        changeDetectorRef: ChangeDetectorRef,
        app: App,
        timeoutService: TimeoutService,
        elementRef: ElementRef,
        private draggingService: DraggingService,
        private productVideoService: ProductListingVideoService,
        private cacheService: CacheService,
        protected visibilityService: VisibilityService
    ) {
        super(changeDetectorRef, app, timeoutService, elementRef);

        this.setColourWithDelay = this.disposableTimeoutService.debounce(color => (this.currentColor = color), 500);
    }

    override ngOnInit() {
        this.updateVideoObserver();

        this.subscriptions.add(this.app.customer.model.obs.status.pipe(skip(1)).subscribe(() => this.updatePrice()));
        this.subscriptions.add(this.detectChanges$.pipe(skip(1)).subscribe(() => this.detectChanges()));

        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (this.productService.canTriggerEvent(event, this.model, this.listPosition)) {
                    if (event.id === PRODUCT_CLOSE_PREVIEW_EVENT_NAME) {
                        if (event.data.callback) {
                            event.data.callback(this);
                        }

                        this.closePreview();
                    }

                    if (event.id === PRODUCT_PREPARE_PREVIEW_EVENT_NAME) {
                        if (event.data.callback) {
                            event.data.callback(this);
                        }

                        this.loadPreview(() => {
                            this.dfEventService.dispatch({
                                id: PRODUCT_OPEN_PREVIEW_EVENT_NAME,
                                data: event.data,
                                action: () => {
                                    this.basketAdded = false;

                                    this.draggingService.lock(this);

                                    this.requestedPreview = true;

                                    this.detectChanges();
                                }
                            });
                        });
                    }

                    if (event.id === PRODUCT_WISHLIST_PREPARE_PREVIEW_EVENT_NAME) {
                        this.dfEventService.dispatch({
                            id: PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME,
                            data: {
                                ...event.data,
                                loading: true
                            }
                        });

                        this.loadPreview(() => {
                            this.dfEventService.dispatch({
                                id: PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME,
                                data: {
                                    ...event.data,
                                    loading: false
                                },
                                action: () => {
                                    this.basketAdded = false;

                                    this.draggingService.lock(this);

                                    this.requestedPreview = true;

                                    this.detectChanges();
                                }
                            });
                        });
                    }
                } else if (event.id.startsWith('product')) {
                    this.closePreview();
                }

                if (event.id === BASKET_ADD_NOTICE_CLOSE_EVENT_NAME) {
                    if (this.basketAddNotifyTimeout) {
                        clearTimeout(this.basketAddNotifyTimeout);
                        this.basketAddNotifyTimeout = undefined;
                    }

                    this.basketAdded = false;

                    this.closePreview();
                }
            })
        );

        this.subscriptions.add(
            this.basketService.addToBasketBeforeSubject.subscribe(() => {
                this.basketAdded = true;

                if (this.basketAddNotifyTimeout) {
                    clearTimeout(this.basketAddNotifyTimeout);
                    this.basketAddNotifyTimeout = undefined;
                }

                this.basketAddNotifyTimeout = this.disposableTimeoutService.setTimeout(() => {
                    this.basketAdded = false;

                    this.closePreview();
                }, 12000);
            })
        );

        this.abServiceSubscription = this.abService.getSubject('labelDiscount').subscribe(value => {
            if (value.variant === 'B') {
                this.labelDiscount = true;
            } else {
                this.labelDiscount = false;
            }

            this.detectChanges();
        });

        super.ngOnInit();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if ((changes['modelView'] && !changes['modelView'].firstChange) || (changes['altView'] && !changes['altView'].firstChange)) {
            this.updateImages();
            // Needed to reset image carousel.
            this._hadInteraction = false;
            this.detectChanges();
        }

        super.ngOnChanges(changes);
    }

    override ngOnDestroy() {
        if (this.basketAddNotifyTimeout) {
            clearTimeout(this.basketAddNotifyTimeout);
        }
        this.detectChanges$.complete();
        this.playerInstance = undefined;
        this._slidesLoaded.clear();
        this.draggingService.release(this);
        this.parentSubscription?.unsubscribe();
        this.videoCheckSubscription?.unsubscribe();
        this.shouldLoopSubscription?.unsubscribe();
        this.playerStateSubscription?.unsubscribe();
        this.productVideoService.unsubscribe(this.elementRef);
        this.abServiceSubscription?.unsubscribe();

        super.ngOnDestroy();
    }

    get configurableSelection(): IProductConfigurableSelection | undefined {
        if (this.currentColor) {
            return {
                color: this.currentColor,
                ...this.selection
            };
        }

        return this.selection;
    }

    get klevuSearchTrackClick(): KlevuSearch.ITrackSearchProductClickData | undefined {
        if (this.searchTerm && this.model) {
            return {
                product: this.model,
                term: this.searchTerm
            };
        }

        return undefined;
    }

    get sizes(): string | string[] | undefined {
        if (this._sizes) {
            return this._sizes;
        }

        const data = this.model?.data;

        if (data && this.listing) {
            const colSpan = Math.max(1, Math.min(data.col_span || 1, this.listing?.size?.getState() || 1));
            const defaultSize = this.listing?.size?.defaultSize || this.moduleConfig.listing.category.defaultSize || [2, 2, 5, 5];

            const currentSizeS = this.listing?.size?.currentSize[0] || 0;
            const defaultSizeS = currentSizeS !== 0 && currentSizeS !== defaultSize[0] ? currentSizeS : defaultSize[0];
            const sizeS = `${Math.min(100, Math.max(20, Math.round(100 / defaultSizeS) * colSpan))}vw`;

            const currentSizeM = this.listing?.size?.currentSize[1] || 0;
            const defaultSizeM = currentSizeM !== 0 && currentSizeM !== defaultSize[1] ? currentSizeM : defaultSize[1];
            const sizeM = `${Math.min(100, Math.max(20, Math.round(100 / defaultSizeM) * colSpan))}vw`;

            const currentSizeL = this.listing?.size?.currentSize[2] || 0;
            const defaultSizeL = currentSizeL !== 0 && currentSizeL !== defaultSize[2] ? currentSizeL : defaultSize[2];
            const sizeL = `${Math.min(100, Math.max(20, Math.round(100 / defaultSizeL) * colSpan))}vw`;

            const currentSizeX = this.listing?.size?.currentSize[3] || 0;
            const defaultSizeX = currentSizeX !== 0 && currentSizeX !== defaultSize[3] ? currentSizeX : defaultSize[3];
            const sizeX = `${Math.min(100, Math.max(20, Math.round(100 / defaultSizeX) * colSpan))}vw`;

            return [sizeS, sizeM, sizeL, sizeX];
        }

        return undefined;
    }

    get klevuCategoryProductTrackClick(): KlevuSearch.ITrackCategoryProductClickData | undefined {
        if (this.sendTracking && this.category && this.model) {
            return {
                product: this.model,
                category: this.category,
                position: !!this.listPosition && this.listPosition > 0 ? this.listPosition - 1 : undefined
            };
        }

        return undefined;
    }

    get klevuRecommendationsProductClickData():
        | KlevuSearch.IAnalyticsRecommendationTrackData<KlevuSearch.EAnalyticsRecommendationEvent.select>
        | undefined {
        if (this.klevuRecommendationId && this.model && typeof this.listPosition === 'number') {
            return <KlevuSearch.IAnalyticsRecommendationTrackData<KlevuSearch.EAnalyticsRecommendationEvent.select>>{
                event: KlevuSearch.EAnalyticsRecommendationEvent.select,
                eventListId: this.klevuRecommendationId,
                products: [{ model: this.model, position: this.listPosition }]
            };
        }

        return undefined;
    }

    get playerIdentifier(): string {
        return `video.${this.model?.id}.${this.listPosition}`;
    }

    /**
     * List of colors to display swatches (with metadata from colors map)
     */
    get colors(): number[] | undefined {
        const colors = this.getAllColors();

        return colors.length > 1 ? colors : undefined;
    }

    private getAllColors(): number[] {
        const parent = <ProductDecorator<IProductDataV2>>this.model?.parent;

        // only when configurable product has at least 2 colour options
        if (Array.isArray(parent?.data?.color) && parent.hasColorOption) {
            return parent.data.color;
        }

        return [];
    }

    set currentColor(color: number) {
        if (color !== this._currentColor) {
            this._currentColor = color;
            this.updateColorDeps();
        }
    }

    get currentColor(): number | undefined {
        return typeof this._currentColor === 'number' ? this._currentColor : undefined;
    }

    protected override onModelChange() {
        this.parentSubscription?.unsubscribe();
        this.parentSubscription = this.model?.parent.change.pipe(skip(1)).subscribe(() => {
            this.detectChanges();
        });

        const color = this.model?.data?.color;

        if (color && typeof color === 'number') {
            // prevent unnecessary detect changes
            this._currentColor = color;
        } else {
            const colors = this.getAllColors();

            if (colors && colors.length) {
                const matchingColor = this.parent?.data?.colorMap?.find(i => i.parent === this.parent?.id)?.color;
                const color = matchingColor && colors.includes(matchingColor) ? matchingColor : colors[0];

                // prevent unnecessary detect changes
                this._currentColor = color;
            }
        }

        const selection = this.getSelection();

        if (selection) {
            this.emailMeWhenInStock =
                !!this.app.data.showOutOfStock && (!this.model?.inStock || !this.model.deepStockCheck(selection, true));
        }

        this.updateColorDeps();

        super.onModelChange();
    }

    onMouseIn() {
        if (!this.client.isTouchy) {
            this.onInteraction();
        }
    }

    onMouseOut() {
        if (!this._previewStatus.busy && !this.client.isS && !this.client.isM) {
            this.closePreview();
        }
    }

    protected onInteraction() {
        if (!this._hadInteraction) {
            this._hadInteraction = true;

            this.detectChanges();
        }
    }

    handleDataChange(data: IProductConfigurableSelection) {
        this.selection = data;

        this.detectChanges();
    }

    handleInitChange(data: IProductConfigurableSelection) {
        this.matchExactly(data);

        this.handleDataChange(data);
    }

    handleSelectionChange(data: IProductConfigurableSelection) {
        this.matchExactly(data);
    }

    //////////////// CAROUSEL ///////////////

    /**
     * If carousel can be printed
     */
    get canShowCarousel(): boolean {
        return (
            this._hadInteraction &&
            !this.model?.data?.disable_carousel &&
            !this.disableCarousel &&
            this.sliderImages.length > 0 &&
            this.app.isPlatformBrowser
        );
    }

    /**
     * Retrieve first image available
     */
    get primaryImage(): string {
        return this.listingImages && this.listingImages[0].video
            ? this.findFirstImage()
            : this.listingImages[0] && this.listingImages[0].image;
    }

    onPlayerInit(component: VimeoPlayerComponent) {
        this.playerInstance = component.playerInstance;
    }

    onVideoEnd(): void {
        this.productVideoService.notifyEnd();
    }

    onBufferEnd(): void {
        if (!this.isVideoBuffered) {
            this.isVideoBuffered = true;

            this.detectChanges();
        }
    }

    private updateVideoObserver() {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        const hasVideo = !!this.primaryVideo;

        if (!!this.videoCheckSubscription !== hasVideo) {
            // if
            if (hasVideo) {
                this.videoCheckSubscription = this.productVideoService
                    .observe(this.elementRef, this.listPosition, this.playerIdentifier)
                    .subscribe((isPlaying: boolean) => {
                        let isPaused = true;

                        if (this.isPlaying !== isPlaying) {
                            this.playerStateSubscription?.unsubscribe();
                        }

                        this.isPlaying = isPlaying;
                        this.isVideoBuffered = isPlaying;

                        this.app.events.broadcast(isPlaying ? Media.Events.play : Media.Events.stop, this.playerIdentifier);

                        if (isPlaying) {
                            this.playerStateSubscription = interval(1000)
                                .pipe(
                                    takeWhile(() => isPaused || (isPaused === undefined && isPlaying)),
                                    take(3)
                                )
                                .subscribe(async () => {
                                    this.app.events.broadcast(Media.Events.play, this.playerIdentifier);

                                    isPaused = await this.playerInstance?.player?.getPaused();
                                });
                        }

                        this.detectChanges();
                    });
            } else {
                this.videoCheckSubscription?.unsubscribe();
                this.videoCheckSubscription = undefined;
                this.shouldLoopSubscription?.unsubscribe();
                this.shouldLoopSubscription = undefined;
            }
        }
    }

    /**
     * Retrieve first video available
     */
    get primaryVideo() {
        return this.listingImages && this.listingImages[0] && this.listingImages[0].video;
    }

    protected hasAltLandscape(): boolean {
        const media = this.getMedia();

        if (media?.length) {
            const landscapeImage = find(media, item => item.labels?.find(label => this.isLandscapeImage(label)));
            const landscapeHoverImage = find(media, item => item.labels?.find(label => this.isLandscapeHoverImage(label)));
            return !!(landscapeImage && landscapeHoverImage);
        }

        return false;
    }

    protected getMedia() {
        const colorInfo = this.getColorInfo();
        return colorInfo?.image || colorInfo?.media ? colorInfo?.media : this.model?.data?.media;
    }

    protected isLandscapeImage(label: string): boolean {
        return label === 'landscape';
    }

    protected isLandscapeHoverImage(label: string): boolean {
        return !!label.match('landscape hover');
    }

    /**
     * Update images list
     */
    protected updateImages() {
        const colorInfo = this.getColorInfo();
        const media = this.getMedia();
        let i = 0;
        // setting up base and alternative image
        let listingPortraitMediaModels = compact(
            this.modelView
                ? [
                      this.getMediaItemByLabel(media, 'model-base') || this.getMediaItemByLabel(media, 'base'),
                      this.getMediaItemByLabel(media, 'model-alternative') || this.getMediaItemByLabel(media, 'alternative')
                  ]
                : [this.getMediaItemByLabel(media, 'base'), this.getMediaItemByLabel(media, 'alternative')]
        );
        // display `*-alternative` as base image
        if (this.altView) {
            listingPortraitMediaModels = listingPortraitMediaModels.reverse();
        }

        // adding model images
        const modelItems = filterMediaByLabel(media, 'hero', false) || [];
        modelItems.forEach(modelItem => {
            if (!listingPortraitMediaModels.find(i => i && i.image === modelItem.image)) {
                listingPortraitMediaModels.push(modelItem);
            }
        });
        // removing undefined elements
        this.listingPortraitImages = compact(
            listingPortraitMediaModels.map((item, index) => {
                if (item) {
                    return {
                        image: item.image,
                        index,
                        video: item.video,
                        labels: item.labels
                    };
                }
                return undefined;
            })
        );

        if (!this.listingPortraitImages?.length) {
            this.listingPortraitImages = [{ image: colorInfo?.image || this.model?.data?.image }];
        }

        if (this.model.colSpan > 1) {
            this.listingLandscapeImages = media
                ?.map(item => {
                    if (item.labels?.find(label => this.isLandscapeImage(label))) {
                        const index = this.hasAltLandscape() && this.modelView ? 1 : 0;
                        return {
                            image: item.image,
                            index,
                            video: item.video
                        };
                    }

                    if (item.labels?.find(label => this.isLandscapeHoverImage(label))) {
                        i = i + 1;
                        const index = this.hasAltLandscape() && this.modelView ? 0 : 1;
                        return {
                            image: item.image,
                            index,
                            video: item.video
                        };
                    }

                    if (item.labels?.find(label => label.match('hero'))) {
                        i = i + 1;
                        return {
                            image: item.image,
                            index: 2,
                            video: item.video
                        };
                    }
                })
                .filter(image => !!image)
                .sort((a, b) => a.index - b.index);
        }

        if (!this.listingLandscapeImages?.length) {
            this.listingLandscapeImages = [{ image: colorInfo?.image || this.model?.data?.image }];
        }
    }

    getMediaItemByLabel(media, label: string): Nexus.Lib.Catalog.Type.IProductMediaItem | undefined {
        const items = filterMediaByLabel(media, label, false);
        return items && items[0];
    }

    /**
     * Updates price value
     */
    protected updatePrice(detectChanges = true) {
        const colorInfo = this.getColorInfo();
        this.currentPrice = (colorInfo ? this.getPrice(colorInfo) : this.model?.price) || 0;
        this.currentOrgPrice = (colorInfo ? this.getOrgPrice(colorInfo) : this.model?.orgPrice) || 0;
        this.isFromPrice = colorInfo ? this.getIsFromPrice(colorInfo) : this.model?.data?.is_from_price;
        this.urlFragment = colorInfo ? `selection.color=${colorInfo.color}` : undefined;

        if (detectChanges) {
            this.detectChanges();
        }
    }

    protected getIsFromPrice(colorInfo: IColorMapEntry): boolean {
        if (typeof this.getGroupPrice(colorInfo) === 'number') {
            const group = this.groupId;
            return group ? colorInfo.group_is_from_price?.[group] : undefined;
        }

        return colorInfo.is_from_price;
    }

    protected getPrice(colorInfo: IColorMapEntry, orgPrice = false): number | undefined {
        const groupPrice = this.getGroupPrice(colorInfo, orgPrice);

        if (typeof groupPrice === 'number') {
            return groupPrice;
        }

        return orgPrice ? colorInfo.org_price : colorInfo.price;
    }

    protected getOrgPrice(colorInfo: IColorMapEntry): number | undefined {
        const orgPriceWithGroups = this.getPrice(colorInfo, true);
        const priceWithGroups = this.getPrice(colorInfo);
        const priceWithoutGroups = colorInfo.price;
        const orgPrice =
            priceWithoutGroups && orgPriceWithGroups
                ? Math.max(priceWithoutGroups, orgPriceWithGroups)
                : priceWithoutGroups || orgPriceWithGroups;

        if (orgPrice && priceWithGroups && orgPrice > priceWithGroups) {
            return orgPrice;
        }

        return undefined;
    }

    protected getGroupPrice(colorInfo: IColorMapEntry, orgPrice = false): number | undefined {
        const group = this.groupId;

        if (group) {
            const groupPrice = orgPrice ? colorInfo.group_org_price?.[group] : colorInfo.group_price?.[group];

            if (typeof groupPrice === 'number') {
                return groupPrice;
            }
        }

        return undefined;
    }

    protected get groupId(): number | undefined {
        return this.app.customer.model.data?.group_id;
    }

    protected updateColorDeps() {
        this.updateImages();
        this.updatePrice(false);
        this.updateCurrentColorProduct();
        this.detectChanges();
    }

    /**
     * We need to load a product related to currently selected product
     * it is not the same product as current parent cause of cross-link binding
     * We identify the product using colors map information
     *
     */
    private updateCurrentColorProduct() {
        const colorInfo = this.getColorInfo();

        let colorProductId: number | undefined;

        if (colorInfo) {
            const colorOption = this.getOnlyColorOption();

            if (colorOption) {
                colorProductId = colorOption[colorInfo.color][0];
            } else {
                colorProductId = colorInfo.parent;
            }
        }

        if (colorProductId) {
            this.currentColorProduct = this.app.resources.product.get(colorProductId) as ProductDecorator<IProductDataV2>;

            if (this.currentColorProduct && !this.currentColorProduct.hasV2) {
                this.currentColorProduct?.load(2).then(() => this.detectChanges());
            }
        } else {
            this.currentColorProduct = undefined;
        }
    }

    /**
     * If product has only one configurable option and its color
     */
    private getOnlyColorOption(): Record<number, number[]> | undefined {
        const options = this.parent?.data?.options?.configurable;

        if (options && !!this.parent?.hasColorOption && Object.values(options).length === 1) {
            return options['color'];
        }

        return undefined;
    }

    /**
     * Retrieves information about color
     */
    protected getColorInfo(color = this.currentColor) {
        return color && this.parent?.data?.colorMap?.find(i => i.color === color);
    }

    /**
     * Get first not video
     */
    private findFirstImage(): string | undefined {
        return this.listingImages?.find(media => !media.video)?.image;
    }

    /**
     * returns correct listing images
     */
    protected get listingImages(): ListingImage[] | undefined {
        return this.model?.isLandscapeListingView ? this.listingLandscapeImages : this.listingPortraitImages;
    }

    /**
     * returns correct listing images for slider
     */
    get sliderImages(): ListingImage[] {
        const images = this.listingImages || [];

        if (images.length && !this.client.isTouchy) {
            return images.slice(1);
        }

        return images;
    }

    ///////////////////////////////
    //
    // PREVIEW
    //
    ///////////////////////////////

    /**
     * If add to basket button can be shown
     */
    get canShowAddToBasket() {
        return this.model?.isSimple || this.canPreview;
    }

    /**
     * If preview options can be used (otherwise simple product approach is taken)
     */
    get canPreview() {
        if (this.parent?.hasSizeOption) {
            const options = this.parent.data?.options?.configurable;

            if (options) {
                const optionsQty = Object.values(options).length;

                switch (optionsQty) {
                    case 1: // size is the only option
                        return true;
                    case 2: // check if the other option is color AND its actually picked
                        return !!options?.['color'] && !!this.currentColor;
                }
            }
        }

        return false;
    }

    /**
     * Load v3 data for preview
     */
    loadPreview(callback?: () => void) {
        if (this.canPreview && !this._previewStatus.busy && !this.previewReady) {
            this.app.request.send(
                this._previewStatus,
                () => this.model?.parent.load(3, { dependencyLevel: EdependencyLevel.details }),
                () => {
                    this.previewReady = true;

                    if (callback) {
                        callback();
                    }
                }
            );
        } else if (this.previewReady && callback) {
            if (callback) {
                callback();
            }
        }
    }

    /**
     * Preview mode close cleanups
     */
    closePreview() {
        if (this.requestedPreview && !this.basketAdded) {
            this.dfEventService.dispatch({
                id: PRODUCT_CLOSE_PREVIEW_EVENT_NAME,
                data: {
                    product: this.model,
                    index: this.listPosition
                },
                action: () => {
                    this.draggingService.release(this);

                    this.requestedPreview = false;

                    this.detectChanges();
                }
            });
        }
    }

    @HostListener('touchstart')
    onTouchStart() {
        this.disposableTimeoutService.setTimeout(() => {
            this.onInteraction();
        }, 500);
    }

    @HostListener('document:touchmove', ['$event'])
    onTouchMove(event: TouchEvent) {
        if (!event.defaultPrevented) {
            this.closePreview();
        }
    }

    get shortLabel(): string | undefined {
        return this.model?.labelShort;
    }

    get labelColor(): string | undefined {
        return this.model?.labelColor;
    }

    protected getSelection(): TAttrOptionSelection | undefined {
        if (!this.listing) {
            return {};
        }

        const filterSelection = this.listing?.filterSelection || [];

        return this.cacheService.get(['filterStockSelection', ...filterSelection], () => {
            const selection: TAttrOptionSelection = {};

            this.listing?.filters.forEach(_filter => {
                if (_filter.config.type === 'attribute-option' || _filter.config.type === 'attribute' || _filter.config.type === 'stock') {
                    const filterSelection =
                        (_filter.selection as number[]) || ((_filter['options'] as Catalog.IAttributeOption[]) || []).map(i => i.value);

                    if (filterSelection && Array.isArray(filterSelection)) {
                        selection[_filter.id] = filterSelection;
                    }
                }
            });

            return selection;
        });
    }
}
